import React, { useState } from 'react';
import { RcFile } from 'antd/lib/upload';
import { useSetRecoilState } from 'recoil';

import DependenciesModal from '../DependenciesModal';
import ProgressPanel from '../ProgressPanel';
import { ExchangeDependency, ExchangeObjectType, ProjectModel } from '../../../gamma-api';
import { defaultApi } from '../../apis';
import IbIcon from '../common/IbIcon';
import { alertsSelectorAdd } from '../../recoil';
import { getTranslateFunc } from '../../utils/stringUtil';
import FileUpload from '../common/FileUpload';

import { buildDependenciesFile, hasActiveDependencies } from './utils';

interface IProjectImportProps {
  folderId: string;
  projectId?: string;
  onFinish?: (project: ProjectModel) => Promise<void>;
}

const ProjectImport: React.FC<IProjectImportProps> = ({ folderId, projectId, onFinish, children }) => {
  const translate = getTranslateFunc('Project: Import:');

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [currentFiles, setCurrentFiles] = useState<RcFile[]>([]);
  const [currentDependencies, setCurrentDependencies] = useState<ExchangeDependency[]>([]);

  const [dependenciesModalVisible, setDependenciesModalVisible] = useState(false);
  const [progressPanelVisible, setProgressPanelVisible] = useState(false);

  const importProject = async (file: RcFile, dependencies: ExchangeDependency[]) => {
    const response = projectId
      ? await defaultApi.importProjectUpdateForm(projectId, folderId, file, buildDependenciesFile(dependencies))
      : await defaultApi.importProjectCreateForm(folderId, file, buildDependenciesFile(dependencies));
    onFinish && (await onFinish(response.data.project));
  };

  const onFileUpload = async (files: RcFile[]) => {
    setProgressPanelVisible(true);
    try {
      const file = files[0];
      const response = projectId
        ? await defaultApi.inspectProjectUpdateForm(projectId, folderId, file)
        : await defaultApi.inspectProjectCreateForm(folderId, file);
      const dependencies = response.data.dependencies;
      if (hasActiveDependencies(dependencies, 1)) {
        setCurrentFiles(files);
        setCurrentDependencies(dependencies);
        setDependenciesModalVisible(true);
      } else {
        await importProject(file, dependencies);
      }
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
    }
    return false;
  };

  const onImportFinish = async () => {
    setDependenciesModalVisible(false);
    setProgressPanelVisible(true);
    try {
      await importProject(currentFiles[0], currentDependencies);
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
      setCurrentDependencies([]);
      setCurrentFiles([]);
    }

    addAlert({
      content: translate('Success'),
      type: 'success',
    });
  };

  const onImportCancel = async () => {
    setDependenciesModalVisible(false);
    setCurrentDependencies([]);
    setCurrentFiles([]);
  };

  const onDependenciesChange = (dependencies: ExchangeDependency[]) => {
    setCurrentDependencies(dependencies);
  };

  return (
    <>
      <FileUpload noButton accept=".zip" disabled={false} multiple={false} onUpload={onFileUpload}>
        {children}
      </FileUpload>
      <DependenciesModal
        dependencies={currentDependencies}
        entityId={projectId}
        entityType={ExchangeObjectType.Project}
        folderId={folderId}
        visible={dependenciesModalVisible}
        onCancel={onImportCancel}
        onChange={onDependenciesChange}
        onFinish={onImportFinish}
      />
      <ProgressPanel.ProjectImport visible={progressPanelVisible} />
    </>
  );
};

export default ProjectImport;
